import { Close } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/joy";
import { ReactNode, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { apiFacetsToRoutesFacets } from "@/components/Archive/apiFacetsToRouteFacets";
import { useArchiveQueryContext } from "@/components/Archive/ArchiveRoutesQueryContext";
import { useArchiveUiStore } from "@/components/Archive/ArchiveUiStore";
import { buildLabelList, splitRouteLabelConfig } from "@/components/RouteLabels/routeLabelsConfig";
import { useRoutesUiStore } from "@/components/RoutesProvider";
import { useRouteLabelConfigQuery } from "@/hooks/routeLabels";
import { useVenueArchiveRoutesFacetsQuery } from "@/hooks/routes";
import { getAllGradeValuesForSystem } from "@/utils/grades";

import { useGradesQuery, useGradeSystemChoices } from "../../../hooks/grades";
import {
  useCurrentClimbType,
  useCurrentVenueQuery,
  useCurrentVenueUuid,
} from "../../../hooks/venues";
import { TraitValue } from "../../../utils/traits";
import ChoiceRangeSlider from "../../forms/ChoicesSlider";
import GradeRangeSlider from "../../forms/GradeRangeSlider";
import TraitSelector from "../../forms/TraitSelector";
import { useRoutesUiContext } from "../../RoutesProvider/RoutesProvider";
import { useRoutesFacets, useRoutesTableStore } from "../RoutesProvider2";
import { FacetMap, getRangeFilterChoices, labelFilterValue } from "../useFilteredRoutes";
import GradeHistogram, { GradeHistogramChart } from "./GradeHistogram";

const Section = ({ name, children }: { name: string; children?: ReactNode }) => {
  return (
    <Accordion>
      {/* <Typography level="title-lg" sx={{ marginBottom: 1 }}> */}
      <AccordionSummary
        slotProps={{
          button: {
            sx: { paddingX: 1.5, paddingY: 1 },
          },
        }}
      >
        <strong>{name}</strong>
      </AccordionSummary>
      {/* </Typography> */}
      <AccordionDetails
        sx={{
          paddingX: 1.5,
          paddingY: 1.5,
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

const TypeSection = ({ isArchive }: { isArchive?: boolean }) => {
  const climbType = useCurrentClimbType();
  const { filterParams, setFilterParam, removeFilterParam } = useRoutesTableStore(
    useShallow((state) => ({
      filterParams: state.filterParams,
      setFilterParam: state.setFilterParam,
      removeFilterParam: state.removeFilterParam,
    })),
  );

  const isAutoBelay = filterParams.ascentStyle?.matches.has("AUTOBELAY");
  const isExComp = filterParams.isExComp;
  const canLead =
    filterParams.ascentStyle?.matches.has("LEAD_TOPROPE") &&
    filterParams.ascentStyle?.matches.has("LEAD");
  const isRecentlyRestored = filterParams.isRecentlyRestored;
  const hasComments = filterParams.hasComments;

  const minEndTime = filterParams.endTime?.min;
  const isRecentlyStripped =
    minEndTime &&
    Math.round(
      Math.abs(new Date().getTime() - new Date(minEndTime).getTime()) / (1000 * 60 * 60),
    ) === 24;

  const addAscentStyle = (style: "AUTOBELAY" | "LEAD_TOPROPE" | "LEAD") => {
    const updatedMatches = new Set(filterParams.ascentStyle?.matches);
    updatedMatches.add(style);
    setFilterParam("ascentStyle", { matches: updatedMatches });
  };

  const removeAscentStyle = (style: "AUTOBELAY" | "LEAD_TOPROPE" | "LEAD") => {
    const updatedMatches = new Set(filterParams.ascentStyle?.matches);
    updatedMatches.delete(style);
    if (updatedMatches.size === 0) {
      removeFilterParam("ascentStyle");
    } else {
      setFilterParam("ascentStyle", { matches: updatedMatches });
    }
  };

  return (
    <Stack spacing={1.5}>
      {climbType === "SPORT" ? (
        <Checkbox
          checked={!!isAutoBelay}
          onChange={(e) =>
            e.target.checked ? addAscentStyle("AUTOBELAY") : removeAscentStyle("AUTOBELAY")
          }
          label="Is auto-belay?"
        />
      ) : null}
      {climbType === "SPORT" ? (
        <Checkbox
          checked={!!canLead}
          onChange={(e) => {
            if (e.target.checked) {
              addAscentStyle("LEAD_TOPROPE");
              addAscentStyle("LEAD");
            } else {
              removeAscentStyle("LEAD_TOPROPE");
              removeAscentStyle("LEAD");
            }
          }}
          label="Can lead?"
        />
      ) : null}

      <Checkbox
        label="Is ex-comp?"
        checked={!!isExComp}
        onChange={(e) =>
          e.target.checked ? setFilterParam("isExComp", true) : removeFilterParam("isExComp")
        }
      />

      {!isArchive ? (
        <>
          <Checkbox
            label="Restored in last 7d"
            checked={!!isRecentlyRestored}
            onChange={(e) =>
              e.target.checked
                ? setFilterParam("isRecentlyRestored", true)
                : removeFilterParam("isRecentlyRestored")
            }
          />
          <Checkbox
            label="Has comments in inbox"
            checked={!!hasComments}
            onChange={(e) =>
              e.target.checked
                ? setFilterParam("hasComments", true)
                : removeFilterParam("hasComments")
            }
          />
        </>
      ) : (
        <Checkbox
          label="Stripped in last 24h"
          checked={!!isRecentlyStripped}
          onChange={(e) => {
            e.target.checked
              ? setFilterParam("endTime", {
                  min: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
                })
              : removeFilterParam("endTime");
          }}
        />
      )}
    </Stack>
  );
};

export const GradeRangeInput = () => {
  const { filterParams, setFilterParam } = useRoutesTableStore(
    useShallow((state) => ({
      filterParams: state.filterParams,
      setFilterParam: state.setFilterParam,
    })),
  );
  const gradeSystemChoices = useGradeSystemChoices();

  const grade = filterParams.grade;
  return (
    <Box sx={{ paddingX: 1.5 }}>
      <GradeRangeSlider
        maxGradeValue={grade?.max}
        minGradeValue={grade?.min}
        gradeSystem={grade?.system || gradeSystemChoices[0]}
        gradeSystemChoices={gradeSystemChoices}
        onChange={(change) =>
          setFilterParam("grade", {
            system: change.gradeSystem,
            min: change.minGradeValue || undefined,
            max: change.maxGradeValue || undefined,
          })
        }
      />
    </Box>
  );
};

const StarRatingRangeInput = () => {
  const { filterParams, setFilterParam } = useRoutesTableStore(
    useShallow((state) => ({
      filterParams: state.filterParams,
      setFilterParam: state.setFilterParam,
    })),
  );
  const choices = getRangeFilterChoices("starRating");
  if (!choices) {
    return null;
  }
  const min = filterParams.starRating?.min;
  const max = filterParams.starRating?.max;

  return (
    <Box>
      <FormLabel>Star rating</FormLabel>
      <Box sx={{ paddingX: 1.5 }}>
        <ChoiceRangeSlider
          choices={choices}
          minValue={min}
          maxValue={max}
          onChange={(change) => {
            setFilterParam("starRating", {
              min: change.minValue ?? undefined,
              max: change.maxValue ?? undefined,
            });
          }}
        />
      </Box>
    </Box>
  );
};

const TraitSelectorInput = <T extends "styles" | "skills" | "holds">({ name }: { name: T }) => {
  const { filterParams, setFilterParam, removeFilterParam } = useRoutesTableStore(
    useShallow((state) => ({
      filterParams: state.filterParams,
      setFilterParam: state.setFilterParam,
      removeFilterParam: state.removeFilterParam,
    })),
  );
  const filterParamValue = filterParams[name];

  return (
    <TraitSelector
      traitType={name}
      selectedTraits={
        [...(filterParamValue ? filterParamValue.matches : [])].filter(
          (x) => x !== null,
        ) as TraitValue<T>[]
      }
      setSelectedTraits={(traits) => {
        if (traits.length === 0) {
          removeFilterParam(name);
        } else {
          setFilterParam(name, { matches: new Set(traits) });
        }
      }}
    />
  );
};

/**
 * Checkboxes for the venue's labels, under their group names, in the order of
 * the labels settings page. Each shows how many of the venue's routes have it.
 */
const LabelsFilterInput = () => {
  const { filterParams, setFilterParam, removeFilterParam } = useRoutesTableStore(
    useShallow((state) => ({
      filterParams: state.filterParams,
      setFilterParam: state.setFilterParam,
      removeFilterParam: state.removeFilterParam,
    })),
  );
  const facets = useRoutesFacets();
  const { data: config } = useRouteLabelConfigQuery();
  const entries = useMemo(() => {
    const { activeGroups, activeRootLabels } = splitRouteLabelConfig(config);
    return buildLabelList(activeGroups, activeRootLabels);
  }, [config]);

  const matches = filterParams.labels?.matches;

  const toggle = (value: string | null) => {
    const updated = new Set(matches);
    if (updated.has(value)) {
      updated.delete(value);
    } else {
      updated.add(value);
    }
    if (updated.size === 0) {
      removeFilterParam("labels");
    } else {
      setFilterParam("labels", { matches: updated });
    }
  };

  const renderCheckbox = (value: string | null, name: string) => (
    <Checkbox
      key={value ?? "none"}
      size="sm"
      label={`${name} (${facets?.labels.get(value)?.count ?? 0})`}
      checked={!!matches?.has(value)}
      onChange={() => toggle(value)}
    />
  );

  return (
    <Stack spacing={1.5}>
      {entries.map((entry) =>
        entry.kind === "group" ? (
          <Stack key={entry.group.uuid} spacing={0.75}>
            <FormLabel>{entry.group.name}</FormLabel>
            {entry.group.labels.map((label) =>
              renderCheckbox(
                labelFilterValue({ uuid: label.uuid, group: entry.group }),
                label.name,
              ),
            )}
          </Stack>
        ) : (
          renderCheckbox(labelFilterValue({ ...entry.label, group: null }), entry.label.name)
        ),
      )}
      {renderCheckbox(null, "No labels")}
    </Stack>
  );
};

const LabelsSection = () => {
  const { data: config } = useRouteLabelConfigQuery();
  const hasLabels =
    config?.groups.some((group) => group.isActive) ||
    config?.labels.some((label) => label.isActive);
  if (!hasLabels) {
    return null;
  }
  return (
    <Section name="Labels">
      <LabelsFilterInput />
    </Section>
  );
};

const ClearFilterButton = () => {
  const { clearFilterParams, filterParams } = useRoutesTableStore(
    useShallow((state) => ({
      clearFilterParams: state.clearFilterParams,
      filterParams: state.filterParams,
    })),
  );
  if (Object.keys(filterParams).length === 0) {
    return;
  }
  return (
    <Box
      sx={{
        paddingX: 1,
        paddingY: 1,
      }}
    >
      <Button
        variant="soft"
        color="neutral"
        onClick={() => clearFilterParams()}
        sx={{ width: "100%" }}
      >
        Clear filters
      </Button>
    </Box>
  );
};

export const FilterSidebar = () => {
  const setDisplayFilterSidebar = useRoutesUiStore((state) => state.setDisplayFilterSidebar);
  const { filteredRouteUuids } = useRoutesUiContext();
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderTopLeftRadius: 16,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          paddingX: 1,
          paddingTop: 0.5,
          justifyContent: "space-between",
        }}
      >
        <Typography level="title-md" fontWeight={"bold"}>
          Filters
        </Typography>
        <IconButton onClick={() => setDisplayFilterSidebar(false)}>
          <Close />
        </IconButton>
      </Box>
      <Box
        sx={{
          borderLeft: "1px solid",
          borderColor: "neutral.200",
          flex: 1,
          flexBasis: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
          }}
        >
          <Box sx={{ paddingX: 1 }}>
            <Box>
              <Typography level="body-md">
                <Typography level="h4">{filteredRouteUuids.size}</Typography> matching routes
              </Typography>
            </Box>
            <GradeHistogram />
          </Box>
          <Section name="Grade">
            <GradeRangeInput />
          </Section>
          <Section name="Type">
            <TypeSection isArchive={false} />
          </Section>
          <Section name="Community">
            <StarRatingRangeInput />
          </Section>
          <Section name="Styles">
            <TraitSelectorInput name="styles" />
          </Section>
          <Section name="Skills">
            <TraitSelectorInput name="skills" />
          </Section>
          <Section name="Holds">
            <TraitSelectorInput name="holds" />
          </Section>
          <LabelsSection />
        </Box>
        <ClearFilterButton />
      </Box>
    </Box>
  );
};

export const ArchiveFilterSidebar = () => {
  const setDisplayFilterSidebar = useArchiveUiStore((state) => state.setDisplayFilterSidebar);
  const ctx = useArchiveQueryContext();
  const filteredRoutesCount = ctx.data?.count;
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderTopLeftRadius: 16,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          paddingX: 1,
          paddingTop: 0.5,
          justifyContent: "space-between",
        }}
      >
        <Typography level="title-md" fontWeight={"bold"}>
          Filters
        </Typography>
        <IconButton onClick={() => setDisplayFilterSidebar(false)}>
          <Close />
        </IconButton>
      </Box>
      <Box
        sx={{
          borderLeft: "1px solid",
          borderColor: "neutral.200",
          flex: 1,
          flexBasis: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
          }}
        >
          <Box sx={{ paddingX: 1 }}>
            <Box>
              <Typography level="body-md">
                <Typography level="h4">{filteredRoutesCount}</Typography> matching routes
              </Typography>
            </Box>
            <ArchiveGradeHistogram />
          </Box>
          <Section name="Grade">
            <GradeRangeInput />
          </Section>
          <Section name="Type">
            <TypeSection isArchive={true} />
          </Section>
          <Section name="Community">
            <StarRatingRangeInput />
          </Section>
          <Section name="Styles">
            <TraitSelectorInput name="styles" />
          </Section>
          <Section name="Skills">
            <TraitSelectorInput name="skills" />
          </Section>
          <Section name="Holds">
            <TraitSelectorInput name="holds" />
          </Section>
        </Box>
        <ClearFilterButton />
      </Box>
    </Box>
  );
};

const gradeValueFacetsToCounts = (
  facetsMap: FacetMap<string | null> | undefined,
  gradeSystem: string | undefined,
) => {
  if (!gradeSystem) {
    return [];
  }
  const counts = new Map(getAllGradeValuesForSystem(gradeSystem).map((grade) => [grade, 0]));
  counts.set("?", 0);
  facetsMap?.forEach((x) => {
    counts.set(x.value || "?", x.count);
  });
  return Array.from(counts.entries(), ([grade, count]) => ({ grade, count }));
};

const ArchiveGradeHistogram = () => {
  const venueUuid = useCurrentVenueUuid();
  const { data: venue } = useCurrentVenueQuery();
  const climbType = useCurrentClimbType();
  const { isFetched: isGradesLoaded } = useGradesQuery();

  const { data: baseFacets } = useVenueArchiveRoutesFacetsQuery(
    venueUuid,
    climbType,
    {},
    apiFacetsToRoutesFacets,
  );
  const currentFacets = useRoutesFacets();
  const gradeSystem =
    (climbType === "BOULDER" ? venue?.boulderGradeSystem : venue?.sportGradeSystem) ?? undefined;

  const allCountsByGrade = useMemo(
    () => (isGradesLoaded ? gradeValueFacetsToCounts(baseFacets?.gradeValue, gradeSystem) : []),
    [baseFacets?.gradeValue, gradeSystem, isGradesLoaded],
  );

  const countsByGrade = useMemo(
    () => (isGradesLoaded ? gradeValueFacetsToCounts(currentFacets?.gradeValue, gradeSystem) : []),
    [currentFacets?.gradeValue, gradeSystem, isGradesLoaded],
  );

  if (!climbType) {
    return;
  }

  return (
    <GradeHistogramChart
      climbType={climbType}
      allCountsByGrade={allCountsByGrade}
      countsByGrade={countsByGrade}
    />
  );
};
